import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Form, Input, Modal, Popconfirm, Radio, Select, Table, Tag, Tooltip, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import subscriptionApi from "../../api/subscriptionApi";
import analyticsApi from "../../api/analyticsApi";

const VISIBLE_TRAINEES = 3;
const SCROLL_AFTER_ROWS = 5;

const TrainingHistory = ({ formatDate }) => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const trainingGiven = Form.useWatch("trainingGiven", form);
  const activeIds = new Set(employees.map((employee) => employee._id));
  const employeeOptions = [
    ...employees.map((employee) => ({ value: employee._id, label: employee.name })),
    ...(editing?.employees || [])
      .filter((employee) => !activeIds.has(employee._id))
      .map((employee) => ({ value: employee._id, label: `${employee.full_name || "Former employee"} (inactive)` })),
  ];

  const load = useCallback(async () => {
    setLoading(true);
    const res = await subscriptionApi.getTraining();
    if (res.status === 200) setRows(res.data || []);
    else message.error(res.message || "Could not load training history");
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    analyticsApi.getEmployees().then(setEmployees);
  }, [load]);

  const openForm = (record) => {
    setEditing(record || null);
    form.resetFields();
    form.setFieldsValue({
      trainingGiven: record ? record.trainingGiven : true,
      employees: record ? record.employees.map((employee) => employee._id) : [],
      description: record?.description || "",
    });
    setFormOpen(true);
  };

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    const res = editing
      ? await subscriptionApi.editTraining(editing._id, values)
      : await subscriptionApi.addTraining(values);
    setSaving(false);
    if (res.status !== 200) return message.error(res.message || "Could not save training");
    message.success(res.message);
    setFormOpen(false);
    load();
  };

  const remove = async (record) => {
    const res = await subscriptionApi.deleteTraining(record._id);
    if (res.status !== 200) return message.error(res.message || "Could not delete training");
    message.success(res.message);
    load();
  };

  const renderTrainees = (list) => {
    if (!list.length) return "-";
    const hidden = list.slice(VISIBLE_TRAINEES);
    return (
      <div className="sd-trainees">
        {list.slice(0, VISIBLE_TRAINEES).map((employee) => (
          <Tag key={employee._id}>{employee.full_name}</Tag>
        ))}
        {hidden.length > 0 && (
          <Tooltip title={hidden.map((employee) => employee.full_name).join(", ")}>
            <Tag>+{hidden.length}</Tag>
          </Tooltip>
        )}
      </div>
    );
  };

  const columns = [
    { title: "Date", dataIndex: "trainedOn", key: "trainedOn", width: 120, render: formatDate },
    {
      title: "Status",
      dataIndex: "trainingGiven",
      key: "trainingGiven",
      width: 110,
      render: (value) => <Tag color={value ? "green" : "default"}>{value ? "Given" : "Not given"}</Tag>,
    },
    { title: "Given to", dataIndex: "employees", key: "employees", render: renderTrainees },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (value) =>
        value ? (
          <Tooltip title={value}>
            <span className="sd-description">{value}</span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Action",
      key: "actions",
      width: 90,
      render: (_, record) => (
        <div className="sd-actions">
          <Tooltip title="Edit">
            <Button className="sd-icon-btn" icon={<EditOutlined />} onClick={() => openForm(record)} />
          </Tooltip>
          <Popconfirm title="Delete this training entry?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(record)}>
            <Tooltip title="Delete">
              <Button className="sd-icon-btn" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <section className="sd-section">
      <div className="sd-section-head">
        <div>
          <h4 className="sd-section-title">
            Implementation history - Training
            {rows.length > 0 && <span className="sd-section-count">{rows.length}</span>}
          </h4>
          <span className="sd-section-sub">Product training given to your team</span>
        </div>
        <Button type="primary" className="sd-add-btn" icon={<PlusOutlined />} onClick={() => openForm()}>
          Add training
        </Button>
      </div>
      <Table
        size="small"
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={rows.length > SCROLL_AFTER_ROWS ? { x: true, y: 240 } : { x: true }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No training recorded yet" /> }}
      />
      <Modal
        title={editing ? "Edit training" : "Add training"}
        visible={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={save}
        confirmLoading={saving}
        okText="Save"
        forceRender
      >
        <Form form={form} layout="vertical" initialValues={{ trainingGiven: true, employees: [], description: "" }}>
          <Form.Item name="trainingGiven" label="Training given?">
            <Radio.Group>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>
          {trainingGiven && (
            <Form.Item
              name="employees"
              label="Given to"
              rules={[{ required: true, message: "Select at least one employee" }]}
            >
              <Select
                mode="multiple"
                placeholder="Select employees"
                optionFilterProp="label"
                options={employeeOptions}
              />
            </Form.Item>
          )}
          <Form.Item name="description" label="Description" rules={[{ max: 1000, message: "Maximum 1000 characters" }]}>
            <Input.TextArea rows={3} placeholder="Topics covered, mode of training, remarks" />
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
};

export default TrainingHistory;
